import { createMemo, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import type { JSX } from "solid-js";
import type { RequestTraceDetail, RequestTraceSpan, RequestTraceSpanKind } from "../shared";

type TimelineRow = {
  span: RequestTraceSpan;
  depth: number;
  relativeStartMs: number;
};

type TimelineNode = TimelineRow & {
  children: TimelineNode[];
  subtreeDurationMs: number;
  descendantCount: number;
};

type TimelineMode = "structured" | "bars";
type StructuredMode = "grouped" | "flat";

type HoveredSpan = {
  span: RequestTraceSpan;
  x: number;
  y: number;
  relativeStartMs: number;
  subtreeDurationMs?: number;
  descendantCount?: number;
} | null;

const KIND_COLORS: Record<RequestTraceSpanKind, string> = {
  request: "bg-cyan-500/80",
  "db.query": "bg-blue-500/80",
  "db.batch": "bg-indigo-500/80",
  "cache.get": "bg-emerald-500/80",
  "cache.put": "bg-green-500/80",
  "cache.delete": "bg-lime-500/80",
  "external.http": "bg-amber-500/80",
  custom: "bg-fuchsia-500/80",
};

const DETAIL_PANEL_MIN_HEIGHT = 560;
const DETAIL_PANEL_DEFAULT_HEIGHT = 760;
const DETAIL_PANEL_MAX_VIEWPORT_MARGIN = 120;
const DETAIL_COLUMN_MIN_WIDTH = 320;
const DETAIL_COLUMN_DEFAULT_WIDTH = 460;
const DETAIL_COLUMN_MAX_WIDTH = 820;

function safeTime(value: string | null) {
  return value ? Date.parse(value) : NaN;
}

function buildTimelineRows(detail: RequestTraceDetail): TimelineRow[] {
  const startMs = safeTime(detail.trace.requestStartedAt);
  const spansById = new Map(detail.spans.map((span) => [span.id, span]));
  const depthCache = new Map<string, number>();
  const computeDepth = (span: RequestTraceSpan): number => {
    const cached = depthCache.get(span.id);
    if (cached != null) return cached;
    if (!span.parentSpanId) {
      depthCache.set(span.id, 0);
      return 0;
    }
    const parent = spansById.get(span.parentSpanId);
    const depth = parent ? computeDepth(parent) + 1 : 0;
    depthCache.set(span.id, depth);
    return depth;
  };

  return detail.spans
    .map((span) => {
      const spanStartMs = safeTime(span.startedAt);
      return {
        span,
        depth: computeDepth(span),
        relativeStartMs: Math.max(0, spanStartMs - startMs),
      };
    })
    .sort((a, b) => {
      if (a.relativeStartMs !== b.relativeStartMs) return a.relativeStartMs - b.relativeStartMs;
      return a.span.id.localeCompare(b.span.id);
    });
}

function summarizeByKind(spans: RequestTraceSpan[]) {
  const summary = new Map<RequestTraceSpanKind, { count: number; totalMs: number }>();
  for (const span of spans) {
    const current = summary.get(span.kind) ?? { count: 0, totalMs: 0 };
    current.count += 1;
    current.totalMs += span.durationMs;
    summary.set(span.kind, current);
  }
  return Array.from(summary.entries())
    .map(([kind, value]) => ({ kind, ...value }))
    .sort((a, b) => b.totalMs - a.totalMs);
}

function buildTimelineTree(rows: TimelineRow[]) {
  const nodes = new Map<string, TimelineNode>();
  const roots: TimelineNode[] = [];

  for (const row of rows) {
    nodes.set(row.span.id, {
      ...row,
      children: [],
      subtreeDurationMs: row.span.durationMs,
      descendantCount: 0,
    });
  }

  for (const row of rows) {
    const node = nodes.get(row.span.id);
    if (!node) continue;
    if (!row.span.parentSpanId) {
      roots.push(node);
      continue;
    }
    const parent = nodes.get(row.span.parentSpanId);
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }

  const finalizeNode = (node: TimelineNode) => {
    node.children.sort((a, b) => {
      if (a.relativeStartMs !== b.relativeStartMs) return a.relativeStartMs - b.relativeStartMs;
      return a.span.id.localeCompare(b.span.id);
    });
    let subtreeDurationMs = node.span.durationMs;
    let descendantCount = node.children.length;
    for (const child of node.children) {
      finalizeNode(child);
      subtreeDurationMs += child.subtreeDurationMs;
      descendantCount += child.descendantCount;
    }
    node.subtreeDurationMs = subtreeDurationMs;
    node.descendantCount = descendantCount;
  };

  for (const root of roots) finalizeNode(root);
  roots.sort((a, b) => {
    if (a.relativeStartMs !== b.relativeStartMs) return a.relativeStartMs - b.relativeStartMs;
    return a.span.id.localeCompare(b.span.id);
  });
  return roots;
}

function clampTimelineHeight(nextHeight: number) {
  if (typeof window === "undefined") {
    return Math.max(DETAIL_PANEL_MIN_HEIGHT, nextHeight);
  }
  const maxHeight = Math.max(DETAIL_PANEL_MIN_HEIGHT, window.innerHeight - DETAIL_PANEL_MAX_VIEWPORT_MARGIN);
  return Math.max(DETAIL_PANEL_MIN_HEIGHT, Math.min(maxHeight, nextHeight));
}

function clampDetailWidth(nextWidth: number) {
  return Math.max(DETAIL_COLUMN_MIN_WIDTH, Math.min(DETAIL_COLUMN_MAX_WIDTH, nextWidth));
}

function durationBadge(durationMs: number) {
  return `${durationMs} ms`;
}

type TraceExportPayload = {
  trace: RequestTraceDetail["trace"];
  spans: RequestTraceDetail["spans"];
  analysisSummary: {
    totalDurationMs: number;
    spanCount: number;
    longestSpans: Array<{
      id: string;
      name: string;
      kind: RequestTraceSpanKind;
      durationMs: number;
      status: string | null;
    }>;
    totalsByKind: Array<{
      kind: RequestTraceSpanKind;
      count: number;
      totalMs: number;
    }>;
  };
};

function buildTraceExportPayload(
  detail: RequestTraceDetail,
  longestSpans: Array<RequestTraceSpan>,
  kindSummary: ReturnType<typeof summarizeByKind>,
): TraceExportPayload {
  return {
    trace: detail.trace,
    spans: detail.spans,
    analysisSummary: {
      totalDurationMs: detail.trace.durationMs,
      spanCount: detail.trace.spanCount,
      longestSpans: longestSpans.map((span) => ({
        id: span.id,
        name: span.name,
        kind: span.kind,
        durationMs: span.durationMs,
        status: span.status,
      })),
      totalsByKind: kindSummary.map((entry) => ({
        kind: entry.kind,
        count: entry.count,
        totalMs: entry.totalMs,
      })),
    },
  };
}

export function PerformanceTraceTimelineView(props: {
  detail: RequestTraceDetail;
  onBack: () => void;
  onOpenWindow: () => void;
  onDeleteTrace: () => Promise<void> | void;
  deletePending?: boolean;
  onDeleteCancel?: () => void;
}) {
  const [selectedSpanId, setSelectedSpanId] = createSignal<string | null>(props.detail.spans[0]?.id ?? null);
  const [timelineMode, setTimelineMode] = createSignal<TimelineMode>("structured");
  const [structuredMode, setStructuredMode] = createSignal<StructuredMode>("grouped");
  const [hoveredSpan, setHoveredSpan] = createSignal<HoveredSpan>(null);
  const [detailHeight, setDetailHeight] = createSignal(DETAIL_PANEL_DEFAULT_HEIGHT);
  const [detailWidth, setDetailWidth] = createSignal(DETAIL_COLUMN_DEFAULT_WIDTH);
  const [copyState, setCopyState] = createSignal<"idle" | "copied" | "error">("idle");

  const rows = createMemo(() => buildTimelineRows(props.detail));
  const tree = createMemo(() => buildTimelineTree(rows()));
  const totalMs = createMemo(() => Math.max(props.detail.trace.durationMs, 1));
  const longestSpans = createMemo(() => [...props.detail.spans].sort((a, b) => b.durationMs - a.durationMs).slice(0, 5));
  const kindSummary = createMemo(() => summarizeByKind(props.detail.spans));
  const selectedSpan = createMemo(() => props.detail.spans.find((span) => span.id === selectedSpanId()) ?? null);
  let copyFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

  onMount(() => {
    setDetailHeight(clampTimelineHeight(DETAIL_PANEL_DEFAULT_HEIGHT));
    setDetailWidth(clampDetailWidth(DETAIL_COLUMN_DEFAULT_WIDTH));
  });

  function startVerticalResize(event: MouseEvent) {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = detailHeight();
    const handleMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientY - startY;
      setDetailHeight(clampTimelineHeight(startHeight + delta));
    };
    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }

  function startHorizontalResize(event: MouseEvent) {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = detailWidth();
    const handleMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      setDetailWidth(clampDetailWidth(startWidth + delta));
    };
    const handleUp = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
  }

  function showTooltip(
    span: RequestTraceSpan,
    event: MouseEvent,
    relativeStartMs: number,
    subtreeDurationMs?: number,
    descendantCount?: number,
  ) {
    setHoveredSpan({
      span,
      x: event.clientX,
      y: event.clientY,
      relativeStartMs,
      subtreeDurationMs,
      descendantCount,
    });
  }

  function moveTooltip(event: MouseEvent) {
    const current = hoveredSpan();
    if (!current) return;
    setHoveredSpan({ ...current, x: event.clientX, y: event.clientY });
  }

  function hideTooltip() {
    setHoveredSpan(null);
  }

  async function copyTraceJson() {
    const payload = buildTraceExportPayload(props.detail, longestSpans(), kindSummary());
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyState("copied");
    } catch {
      setCopyState("error");
    }
    if (copyFeedbackTimer) clearTimeout(copyFeedbackTimer);
    copyFeedbackTimer = setTimeout(() => setCopyState("idle"), 2000);
  }

  onCleanup(() => {
    hideTooltip();
    if (copyFeedbackTimer) clearTimeout(copyFeedbackTimer);
  });

  function renderStructuredNode(node: TimelineNode): JSX.Element {
    const left = `${(node.relativeStartMs / totalMs()) * 100}%`;
    const width = `${Math.max((node.span.durationMs / totalMs()) * 100, 0.75)}%`;
    const isSelected = () => selectedSpanId() === node.span.id;
    const isError = node.span.status === "error";
    const isSlow = node.span.durationMs >= 100;

    return (
      <div class={`${node.depth > 0 ? "ml-5 border-l border-cyan-500/15 pl-4" : ""}`}>
        <button
          type="button"
          onClick={() => setSelectedSpanId(node.span.id)}
          onMouseEnter={(event) => showTooltip(node.span, event, node.relativeStartMs, node.subtreeDurationMs, node.descendantCount)}
          onMouseMove={moveTooltip}
          onMouseLeave={hideTooltip}
          class={`grid w-full grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4 border-b border-gray-800 px-4 py-3 text-left transition-colors hover:bg-gray-800/40 ${
            isSelected() ? "bg-gray-800/60" : node.depth > 0 ? "bg-gray-900/20" : ""
          }`}
        >
          <div class="min-w-0 overflow-hidden">
            <div class="flex min-w-0 items-start gap-3">
              <span class={`mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${isError ? "bg-red-400" : isSlow ? "bg-amber-400" : "bg-cyan-400"}`} />
              <div class="min-w-0 flex-1 overflow-hidden">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="truncate font-medium text-gray-100">{node.span.name}</span>
                  <span class="rounded-full bg-gray-800/90 px-2 py-0.5 text-[11px] text-gray-300">
                    {durationBadge(node.span.durationMs)}
                  </span>
                  <Show when={node.children.length > 0}>
                    <span class="rounded-full bg-cyan-500/10 px-2 py-0.5 text-[11px] text-cyan-200">
                      {node.children.length} nested
                    </span>
                    <span class="rounded-full bg-indigo-500/10 px-2 py-0.5 text-[11px] text-indigo-200">
                      Sigma {node.subtreeDurationMs} ms
                    </span>
                  </Show>
                </div>
                <div class="mt-1 truncate text-xs text-gray-400">
                  {node.span.kind} - {node.span.status ?? "ok"}
                </div>
              </div>
            </div>
          </div>
          <div class="flex items-center">
            <div class="relative h-8 w-full overflow-hidden rounded bg-gray-950/80">
              <div
                class={`absolute top-1.5 h-5 rounded ${KIND_COLORS[node.span.kind] ?? "bg-gray-500/80"} ${isError ? "ring-2 ring-red-300/70" : isSlow ? "ring-1 ring-amber-300/60" : ""}`}
                style={{ left, width }}
              />
            </div>
          </div>
        </button>

        <Show when={node.children.length > 0}>
          <div class="space-y-1 bg-gray-950/10 py-1">
            <For each={node.children}>{(child) => renderStructuredNode(child)}</For>
          </div>
        </Show>
      </div>
    );
  }

  return (
    <div class="flex h-full min-h-0 flex-col gap-4">
      <div class="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 class="text-lg font-semibold text-gray-100">Trace Timeline</h2>
          <p class="text-sm text-gray-400">Visualize nested spans, hot spots, and captured stacks for a traced request.</p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void copyTraceJson()}
            class={`rounded border px-4 py-2 text-sm transition-colors ${
              copyState() === "copied"
                ? "border-emerald-600 bg-emerald-600/20 text-emerald-100"
                : copyState() === "error"
                  ? "border-amber-700 bg-amber-950/40 text-amber-100"
                  : "border-gray-700 text-gray-200 hover:bg-gray-800"
            }`}
            title="Copy trace JSON for external analysis"
          >
            {copyState() === "copied" ? "Copied JSON" : copyState() === "error" ? "Copy Failed" : "Copy JSON"}
          </button>
          <button
            type="button"
            onClick={props.onOpenWindow}
            class="rounded border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
          >
            Open in New Window
          </button>
          <button
            type="button"
            onClick={() => void props.onDeleteTrace()}
            class="rounded border border-red-700 px-4 py-2 text-sm text-red-200 hover:bg-red-950/50"
          >
            {props.deletePending ? "Confirm Delete" : "Delete Trace"}
          </button>
          <Show when={props.deletePending}>
            <button
              type="button"
              onClick={props.onDeleteCancel}
              class="rounded border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
            >
              Cancel
            </button>
          </Show>
          <button
            type="button"
            onClick={props.onBack}
            class="rounded border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
          >
            Back to Trace List
          </button>
        </div>
      </div>

      <section class="grid gap-3 rounded-xl border border-gray-800 bg-gray-900/40 p-4 xl:grid-cols-4">
        <div class="xl:col-span-2">
          <div class="text-xs uppercase tracking-wide text-gray-500">Request</div>
          <div class="mt-1 break-all text-sm font-semibold text-gray-100">{props.detail.trace.method} {props.detail.trace.path}</div>
          <div class="mt-2 grid gap-2 text-sm text-gray-300 md:grid-cols-2">
            <div>Status: {props.detail.trace.statusCode ?? "-"}</div>
            <div>Duration: {props.detail.trace.durationMs} ms</div>
            <div>Spans: {props.detail.trace.spanCount}</div>
            <div>Trigger: {props.detail.trace.triggerSource}</div>
            <div>Stack Mode: {props.detail.trace.stackMode}</div>
            <div>Trace ID: <span class="break-all text-xs">{props.detail.trace.id}</span></div>
          </div>
        </div>
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500">Longest Spans</div>
          <div class="mt-2 space-y-2">
            <For each={longestSpans()}>
              {(span) => (
                <button
                  type="button"
                  onClick={() => setSelectedSpanId(span.id)}
                  class={`w-full rounded border px-3 py-2 text-left text-sm transition-colors ${
                    selectedSpanId() === span.id ? "border-cyan-500 bg-cyan-500/10 text-cyan-100" : "border-gray-800 bg-gray-950/70 text-gray-300 hover:bg-gray-800/60"
                  }`}
                >
                  <div class="font-medium">{span.name}</div>
                  <div class="text-xs text-gray-400">{span.kind} - {span.durationMs} ms</div>
                </button>
              )}
            </For>
          </div>
        </div>
        <div>
          <div class="text-xs uppercase tracking-wide text-gray-500">Totals by Kind</div>
          <div class="mt-2 space-y-2">
            <For each={kindSummary()}>
              {(entry) => (
                <div class="rounded border border-gray-800 bg-gray-950/70 px-3 py-2 text-sm text-gray-300">
                  <div class="font-medium text-gray-100">{entry.kind}</div>
                  <div class="text-xs text-gray-400">{entry.count} spans - {entry.totalMs} ms</div>
                </div>
              )}
            </For>
          </div>
        </div>
      </section>

      <div
        role="separator"
        aria-orientation="horizontal"
        class="h-2 cursor-row-resize rounded-full bg-gray-800/90 transition-colors hover:bg-cyan-500/70"
        onMouseDown={startVerticalResize}
      />

      <div class="min-h-0 flex-none" style={{ height: `${detailHeight()}px`, "min-height": `${DETAIL_PANEL_MIN_HEIGHT}px` }}>
        <div class="flex h-full min-h-0 gap-4">
          <section class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-800 bg-gray-900/40">
            <div class="border-b border-gray-800 px-4 py-3">
              <div class="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div class="text-xs uppercase tracking-wide text-gray-500">Visualize</div>
                  <div class="mt-1 text-sm text-gray-300">Inspect request timing as grouped nested spans, flat spans, or simple duration bars.</div>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <div class="inline-flex rounded-lg border border-gray-700 bg-gray-950/70 p-1 text-sm">
                    <button
                      type="button"
                      onClick={() => setTimelineMode("structured")}
                      class={`rounded-md px-3 py-2 transition-colors ${
                        timelineMode() === "structured" ? "bg-cyan-600 text-white" : "text-gray-300 hover:bg-gray-800"
                      }`}
                    >
                      Structured View
                    </button>
                    <button
                      type="button"
                      onClick={() => setTimelineMode("bars")}
                      class={`rounded-md px-3 py-2 transition-colors ${
                        timelineMode() === "bars" ? "bg-cyan-600 text-white" : "text-gray-300 hover:bg-gray-800"
                      }`}
                    >
                      Bar View
                    </button>
                  </div>
                  <Show when={timelineMode() === "structured"}>
                    <div class="inline-flex rounded-lg border border-gray-700 bg-gray-950/70 p-1 text-sm">
                      <button
                        type="button"
                        onClick={() => setStructuredMode("grouped")}
                        class={`rounded-md px-3 py-2 transition-colors ${
                          structuredMode() === "grouped" ? "bg-indigo-600 text-white" : "text-gray-300 hover:bg-gray-800"
                        }`}
                      >
                        Grouped
                      </button>
                      <button
                        type="button"
                        onClick={() => setStructuredMode("flat")}
                        class={`rounded-md px-3 py-2 transition-colors ${
                          structuredMode() === "flat" ? "bg-indigo-600 text-white" : "text-gray-300 hover:bg-gray-800"
                        }`}
                      >
                        Flat
                      </button>
                    </div>
                  </Show>
                </div>
              </div>
            </div>

            <Show
              when={timelineMode() === "structured"}
              fallback={
                <div class="min-h-0 flex-1 overflow-auto p-4">
                  <div class="space-y-3">
                    <For each={rows()}>
                      {(row, index) => {
                        const left = `${(row.relativeStartMs / totalMs()) * 100}%`;
                        const width = `${Math.max((row.span.durationMs / totalMs()) * 100, 0.75)}%`;
                        const isSelected = () => selectedSpanId() === row.span.id;
                        const isError = row.span.status === "error";
                        const isSlow = row.span.durationMs >= 100;
                        return (
                          <button
                            type="button"
                            onClick={() => setSelectedSpanId(row.span.id)}
                            onMouseEnter={(event) => showTooltip(row.span, event, row.relativeStartMs)}
                            onMouseMove={moveTooltip}
                            onMouseLeave={hideTooltip}
                            class={`block w-full rounded-lg border p-3 text-left transition-colors ${
                              isSelected() ? "border-cyan-500 bg-cyan-500/10" : "border-gray-800 bg-gray-950/70 hover:bg-gray-800/60"
                            }`}
                          >
                            <div class="mb-2 flex items-center justify-between gap-3">
                              <div class="min-w-0 overflow-hidden">
                                <div class="truncate text-sm font-medium text-gray-100">
                                  {index() + 1}. {row.span.name}
                                </div>
                                <div class="truncate text-xs text-gray-400">
                                  {row.span.kind} - {row.span.status ?? "ok"}
                                </div>
                              </div>
                              <div class="shrink-0 rounded-full bg-gray-800/90 px-2 py-1 text-[11px] text-gray-300">
                                {durationBadge(row.span.durationMs)}
                              </div>
                            </div>
                            <div class="relative h-10 overflow-hidden rounded bg-gray-950/80">
                              <div
                                class={`absolute top-2 h-6 rounded ${KIND_COLORS[row.span.kind] ?? "bg-gray-500/80"} ${isError ? "ring-2 ring-red-300/70" : isSlow ? "ring-1 ring-amber-300/60" : ""}`}
                                style={{ left, width }}
                              />
                            </div>
                          </button>
                        );
                      }}
                    </For>
                  </div>
                </div>
              }
            >
              <div class="grid grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4 border-b border-gray-800 px-4 py-3 text-xs uppercase tracking-wide text-gray-500">
                <div>Span</div>
                <div>Timeline</div>
              </div>
              <div class="min-h-0 flex-1 overflow-auto p-2">
                <Show
                  when={structuredMode() === "grouped"}
                  fallback={
                    <div class="space-y-2">
                      <For each={rows()}>
                        {(row) => {
                          const left = `${(row.relativeStartMs / totalMs()) * 100}%`;
                          const width = `${Math.max((row.span.durationMs / totalMs()) * 100, 0.75)}%`;
                          const isSelected = () => selectedSpanId() === row.span.id;
                          const isError = row.span.status === "error";
                          const isSlow = row.span.durationMs >= 100;
                          return (
                            <button
                              type="button"
                              onClick={() => setSelectedSpanId(row.span.id)}
                              onMouseEnter={(event) => showTooltip(row.span, event, row.relativeStartMs)}
                              onMouseMove={moveTooltip}
                              onMouseLeave={hideTooltip}
                              class={`grid w-full grid-cols-[minmax(0,360px)_minmax(0,1fr)] gap-4 rounded-lg border border-gray-800 px-4 py-3 text-left transition-colors hover:bg-gray-800/40 ${
                                isSelected() ? "bg-gray-800/60" : "bg-gray-950/50"
                              }`}
                            >
                              <div class="min-w-0 overflow-hidden">
                                <div class="flex min-w-0 items-start gap-3">
                                  <span class={`mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${isError ? "bg-red-400" : isSlow ? "bg-amber-400" : "bg-cyan-400"}`} />
                                  <div class="min-w-0 flex-1 overflow-hidden">
                                    <div class="flex flex-wrap items-center gap-2">
                                      <span class="truncate font-medium text-gray-100">{row.span.name}</span>
                                      <span class="rounded-full bg-gray-800/90 px-2 py-0.5 text-[11px] text-gray-300">
                                        {durationBadge(row.span.durationMs)}
                                      </span>
                                    </div>
                                    <div class="mt-1 truncate text-xs text-gray-400">
                                      {row.span.kind} - {row.span.status ?? "ok"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div class="flex items-center">
                                <div class="relative h-8 w-full overflow-hidden rounded bg-gray-950/80">
                                  <div
                                    class={`absolute top-1.5 h-5 rounded ${KIND_COLORS[row.span.kind] ?? "bg-gray-500/80"} ${isError ? "ring-2 ring-red-300/70" : isSlow ? "ring-1 ring-amber-300/60" : ""}`}
                                    style={{ left, width }}
                                  />
                                </div>
                              </div>
                            </button>
                          );
                        }}
                      </For>
                    </div>
                  }
                >
                  <div class="space-y-2">
                    <For each={tree()}>{(node) => renderStructuredNode(node)}</For>
                  </div>
                </Show>
              </div>
            </Show>
          </section>

          <div
            role="separator"
            aria-orientation="vertical"
            class="hidden w-2 shrink-0 cursor-col-resize rounded-full bg-gray-800/90 transition-colors hover:bg-cyan-500/70 xl:block"
            onMouseDown={startHorizontalResize}
          />

          <section
            class="min-h-0 overflow-auto rounded-xl border border-gray-800 bg-gray-900/40 p-4"
            style={{ width: `${detailWidth()}px`, "min-width": `${DETAIL_COLUMN_MIN_WIDTH}px` }}
          >
            <h3 class="text-sm font-semibold text-gray-100">Selected Detail</h3>
            <Show when={selectedSpan()} fallback={<p class="mt-3 text-sm text-gray-500">Select a span to inspect its detail.</p>}>
              {(span) => (
                <div class="mt-3 space-y-4 text-sm text-gray-300">
                  <div class="space-y-1">
                    <div class="text-base font-semibold text-gray-100">{span().name}</div>
                    <div>{span().kind} - {span().durationMs} ms - {span().status ?? "ok"}</div>
                    <div>Started: {new Date(span().startedAt).toLocaleString()}</div>
                    <div>Completed: {span().completedAt ? new Date(span().completedAt).toLocaleString() : "-"}</div>
                  </div>
                  <Show when={span().metaJson}>
                    <div>
                      <div class="mb-2 font-medium text-gray-100">Metadata</div>
                      <pre class="overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">{span().metaJson}</pre>
                    </div>
                  </Show>
                  <Show when={span().stackTrace}>
                    <div>
                      <div class="mb-2 font-medium text-gray-100">Stacktrace</div>
                      <pre class="max-h-96 overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">{span().stackTrace}</pre>
                    </div>
                  </Show>
                </div>
              )}
            </Show>

            <Show when={props.detail.trace.stackTrace}>
              <div class="mt-6 border-t border-gray-800 pt-4">
                <div class="mb-2 text-sm font-semibold text-gray-100">Request Stacktrace</div>
                <pre class="max-h-72 overflow-auto rounded bg-gray-950 p-3 text-xs text-gray-300">{props.detail.trace.stackTrace}</pre>
              </div>
            </Show>
          </section>
        </div>
      </div>

      <Show when={hoveredSpan()}>
        {(tooltip) => (
          <div
            class="pointer-events-none fixed z-50 max-w-xs rounded-lg border border-gray-700 bg-gray-950/95 px-3 py-2 text-xs text-gray-200 shadow-2xl"
            style={{
              left: `${Math.min(tooltip().x + 16, window.innerWidth - 280)}px`,
              top: `${Math.max(16, tooltip().y + 16)}px`,
            }}
          >
            <div class="font-semibold text-gray-100">{tooltip().span.name}</div>
            <div class="mt-1">{tooltip().span.kind}</div>
            <div>Duration: {tooltip().span.durationMs} ms</div>
            <div>Start Delta: +{tooltip().relativeStartMs} ms</div>
            <Show when={typeof tooltip().subtreeDurationMs === "number" && tooltip().subtreeDurationMs !== tooltip().span.durationMs}>
              <div>Aggregate Duration: {tooltip().subtreeDurationMs} ms</div>
            </Show>
            <Show when={(tooltip().descendantCount ?? 0) > 0}>
              <div>Nested Spans: {tooltip().descendantCount}</div>
            </Show>
            <div>Status: {tooltip().span.status ?? "ok"}</div>
            <div>Started: {new Date(tooltip().span.startedAt).toLocaleTimeString()}</div>
          </div>
        )}
      </Show>
    </div>
  );
}
